import React from 'react';

// Matches: https?://... | www.... | bare *.com domains (e.g. example.com/path)
// Only .com TLD is auto-linked for bare domains without protocol or www prefix.
// (?<![@.]) on both the www. branch and the bare-domain branch prevents matching
// domain labels that appear inside email addresses
// (e.g. name@example.com, name@sub.example.com, name@www.example.com,
// name@sub.www.example.com).
const URL_REGEX =
  /(?:https?:\/\/\S+|(?<![@.])www\.\S+|(?<![@.])\b[\w-]+(?:\.[\w-]+)*\.com\b[/?\S]*)/g;

// Trailing punctuation characters that are likely sentence-ending, not part of the URL
const TRAILING_PUNCT = /[.,)!?;:'"]+$/;

export interface UrlSegment {
  type: 'text' | 'url';
  value: string;
  href: string;
}

export function extractUrlSegments(text: string): UrlSegment[] {
  const segments: UrlSegment[] = [];
  let lastIndex = 0;

  for (const match of text.matchAll(URL_REGEX)) {
    const start = match.index;
    let rawUrl = match[0];

    // Strip trailing punctuation that is likely sentence-ending
    const trailingMatch = rawUrl.match(TRAILING_PUNCT);
    if (trailingMatch) {
      rawUrl = rawUrl.slice(0, rawUrl.length - trailingMatch[0].length);
    }

    if (start > lastIndex) {
      segments.push({
        type: 'text',
        value: text.slice(lastIndex, start),
        href: '',
      });
    }

    const href = /^https?:\/\//.test(rawUrl) ? rawUrl : `https://${rawUrl}`;
    segments.push({ type: 'url', value: rawUrl, href });

    lastIndex = start + rawUrl.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: 'text', value: text.slice(lastIndex), href: '' });
  }

  if (segments.length === 0) {
    segments.push({ type: 'text', value: text, href: '' });
  }

  return segments;
}

export function containsUrl(text: string): boolean {
  return extractUrlSegments(text).some((s) => s.type === 'url');
}

interface LinkifiedTextProps {
  text: string;
  className?: string;
}

export function LinkifiedText({ text, className }: LinkifiedTextProps) {
  const segments = extractUrlSegments(text);

  return (
    <span className={className}>
      {segments.map((segment, i) =>
        segment.type === 'url' ? (
          <a
            key={i}
            href={segment.href}
            target="_blank"
            rel="noopener noreferrer"
            className="underline decoration-gray-400/50 underline-offset-2 hover:decoration-gray-400"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {segment.value}
          </a>
        ) : (
          <React.Fragment key={i}>{segment.value}</React.Fragment>
        ),
      )}
    </span>
  );
}
